import { QueryClient } from '@tanstack/react-query';

/**
 * Configuration de la QueryClient pour TanStack Query
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Durée de cache par défaut (5 minutes)
      staleTime: 5 * 60 * 1000,
      // Durée de rétention en cache (30 minutes)
      gcTime: 30 * 60 * 1000,
      // Nombre de tentatives de retry
      retry: (failureCount, error) => {
        // Ne pas retry sur les erreurs 4xx
        if (error && typeof error === 'object' && 'status' in error && typeof error.status === 'number') {
          if (error.status >= 400 && error.status < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },
      // Refetch au focus de la fenêtre
      refetchOnWindowFocus: true,
      // Timeout par défaut (30 secondes)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Pas de retry par défaut sur les mutations
      retry: false,
    },
  },
});

// Query keys factory pour une meilleure typage et maintenance
export const queryKeys = {
  // Auth
  auth: {
    me: () => ['auth', 'me'] as const,
    session: () => ['auth', 'session'] as const,
  },
  
  // Practitioners
  practitioners: {
    all: () => ['practitioners'] as const,
    list: (filters: Record<string, unknown>) => ['practitioners', 'list', filters] as const,
    detail: (id: string) => ['practitioners', 'detail', id] as const,
    availabilities: (id: string, params: Record<string, unknown>) => 
      ['practitioners', 'availabilities', id, params] as const,
    reviews: (id: string) => ['practitioners', 'reviews', id] as const,
  },
  
  // Appointments
  appointments: {
    all: () => ['appointments'] as const,
    list: (filters: Record<string, unknown>) => ['appointments', 'list', filters] as const,
    detail: (id: string) => ['appointments', 'detail', id] as const,
    timeline: (id: string) => ['appointments', 'timeline', id] as const,
    documents: (id: string) => ['appointments', 'documents', id] as const,
  },
  
  // Planning (practitioner)
  planning: {
    dashboard: (practitionerId: string) => ['planning', 'dashboard', practitionerId] as const,
    actTypes: (practitionerId: string) => ['planning', 'actTypes', practitionerId] as const,
    availabilityRules: (practitionerId: string) => ['planning', 'availabilityRules', practitionerId] as const,
  },
  
  // User
  user: {
    profile: () => ['user', 'profile'] as const,
    insurance: () => ['user', 'insurance'] as const,
  },
};
